import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CalendarEvent } from 'angular-calendar';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TextareaModule } from 'primeng/textarea';
import { ConfirmationService, MessageService } from 'primeng/api';
import moment from 'moment';
import {
  forkJoin,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import IAppointment from '../../../../_interfaces/IAppointment';
import ICalendar from '../../../../_interfaces/ICalendar';
import IPatient from '../../../../_interfaces/IPatients';
import IPatientHasTreatment from '../../../../_interfaces/IPatientHasTreatment';
import ITreatment from '../../../../_interfaces/ITreatment';
import { BaseComponent } from '../../../base/base.component';
import { SessionService } from '../../../../_services/session/session.service';
import {
  AppointmentCreationDraft,
  AppointmentCreationService,
} from '../../../../_services/appointments/appointment-creation.service';
import { AppointmentService } from '../../../../_services/appointments/appointment.service';
import { CalendarAppointmentsService } from '../../../../_services/calendars/calendar-appointments.service';
import { ErrorsService } from '../../../../_services/errors/errors.service';
import { PatientsService } from '../../../../_services/patients/patients.service';
import { TreatmentsService } from '../../../../_services/treatments/treatments.service';
import {
  MobileAppointmentDraft,
  MobileAppointmentSelection,
} from './calendar-appointments-mobile.models';

type SearchablePatient = IPatient & { identifier: string };

@Component({
  standalone: true,
  selector: 'app-calendar-appointments-mobile-form',
  templateUrl: './calendar-appointments-mobile-form.component.html',
  styleUrl: './calendar-appointments-mobile-form.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    ConfirmDialogModule,
    TextareaModule,
    TranslatePipe,
  ],
  providers: [ConfirmationService],
})
export class CalendarAppointmentsMobileFormComponent
  extends BaseComponent
  implements OnInit, OnChanges, OnDestroy
{
  private static readonly DEFAULT_DURATION_MINUTES = 30;
  private static readonly MIN_PATIENT_QUERY_LENGTH = 2;
  private patientSearchTimeout: ReturnType<typeof setTimeout> | null = null;
  private syncedSelectionKey = '';
  private syncedAppointmentKey = '';
  private loadingAppointmentId: number | null = null;
  private loadedAppointmentDetailId: number | null = null;

  @Input() visible = false;
  @Input() selection: MobileAppointmentSelection | null = null;
  @Input() appointment: IAppointment | null = null;
  @Input() calendarEvent: CalendarEvent | null = null;
  @Input() calendars: ICalendar[] = [];
  @Input() initialPatient: IPatient | null = null;
  @Input() initialTreatments: IPatientHasTreatment[] = [];

  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();

  public draft: MobileAppointmentDraft = this.buildEmptyDraft();
  public calendarOptions: Array<{ id: number; name: string }> = [];
  public startInputValue = '';
  public endInputValue = '';
  public patientQuery = '';
  public patientSuggestions: SearchablePatient[] = [];
  public patientSearchLoading = false;
  public treatmentOptionsLoading = false;
  public availablePatientTreatments: IPatientHasTreatment[] = [];
  public catalogTreatments: ITreatment[] = [];
  public selectedExistingTreatmentId: number | null = null;
  public selectedCatalogTreatmentId: number | null = null;
  public saving = false;
  public conflicts: IAppointment[] = [];

  constructor(
    public session: SessionService,
    private readonly translate: TranslateService,
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly errorsService: ErrorsService,
    private readonly calendarAppointmentsService: CalendarAppointmentsService,
    private readonly appointmentCreationService: AppointmentCreationService,
    private readonly appointmentService: AppointmentService,
    private readonly patientsService: PatientsService,
    private readonly treatmentsService: TreatmentsService,
  ) {
    super(session);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.rebuildCalendarOptions();
    this.syncDraft();
  }

  override ngOnDestroy(): void {
    if (this.patientSearchTimeout) {
      clearTimeout(this.patientSearchTimeout);
      this.patientSearchTimeout = null;
    }
    super.ngOnDestroy();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['calendars']) {
      this.rebuildCalendarOptions();
      this.keepSelectedCalendarInSync();
    }
    if (
      changes['selection'] ||
      changes['visible'] ||
      changes['appointment'] ||
      changes['calendarEvent']
    ) {
      this.syncDraft();
    }
  }

  get canSave(): boolean {
    return !!this.draft.calendar && !!this.draft.start && !!this.draft.end && !this.saving;
  }

  get isEditing(): boolean {
    return !!this.appointment?.id;
  }

  get selectedPatientLabel(): string {
    return this.getPatientLabel(this.draft.patient);
  }

  get selectedTreatmentLabel(): string {
    const existingTreatment = this.selectedExistingTreatmentId
      ? this.availablePatientTreatments.find(
          (treatment) => Number(treatment.id || 0) === this.selectedExistingTreatmentId,
        )
      : null;

    if (existingTreatment) {
      return existingTreatment.treatment?.name || this.translate.instant('COMMON.NO_NAME');
    }

    const catalogTreatment = this.selectedCatalogTreatmentId
      ? this.catalogTreatments.find(
          (treatment) => Number(treatment.id || 0) === this.selectedCatalogTreatmentId,
        )
      : null;

    return (
      catalogTreatment?.name ||
      this.draft.treatments?.[0]?.treatment?.name ||
      ''
    );
  }

  private buildEmptyDraft(): MobileAppointmentDraft {
    return {
      title: '',
      notes: '',
      patient: null,
      treatments: [],
      calendar: null,
      start: null,
      end: null,
    };
  }

  private syncDraft(): void {
    if (this.appointment?.id) {
      this.syncDraftWithAppointment();
      return;
    }

    this.syncDraftWithSelection();
  }

  private syncDraftWithAppointment(): void {
    if (!this.appointment?.id) {
      return;
    }

    const nextAppointmentKey = this.getAppointmentKey(
      this.appointment,
      this.calendarEvent,
    );
    if (nextAppointmentKey === this.syncedAppointmentKey) {
      this.loadAppointmentDetailForEditing(this.appointment.id);
      return;
    }
    this.syncedAppointmentKey = nextAppointmentKey;
    this.syncedSelectionKey = '';

    this.hydrateDraftWithAppointment(this.appointment, this.calendarEvent);
    this.loadAppointmentDetailForEditing(this.appointment.id);
  }

  private hydrateDraftWithAppointment(
    appointment: IAppointment,
    calendarEvent?: CalendarEvent | null,
  ): void {
    const appointmentCalendarId = Number(
      appointment.calendar?.id || appointment.calendars_id || 0,
    );
    const selectedCalendar =
      this.calendars.find((calendar) => Number(calendar.id || 0) === appointmentCalendarId) ||
      appointment.calendar ||
      this.calendars[0] ||
      null;
    const appointmentTreatments = [...(appointment.treatments || [])];
    const start = this.resolveDate(appointment.start, calendarEvent?.start);
    const end = this.resolveDate(appointment.end, calendarEvent?.end);

    this.draft = {
      title: appointment.name || calendarEvent?.title || '',
      notes: appointment.description || '',
      patient: appointment.patient || null,
      treatments: appointmentTreatments,
      calendar: selectedCalendar,
      start,
      end,
    };
    this.startInputValue = this.toDateTimeLocalValue(this.draft.start);
    this.endInputValue = this.toDateTimeLocalValue(this.draft.end);
    this.patientQuery = this.getPatientLabel(this.draft.patient);
    this.patientSuggestions = [];
    this.availablePatientTreatments = [];
    this.catalogTreatments = [];
    this.selectedExistingTreatmentId = appointmentTreatments[0]?.id || null;
    this.selectedCatalogTreatmentId = null;
    this.conflicts = [];
    if (this.draft.patient?.id) {
      this.loadTreatmentOptions();
    }
  }

  private syncDraftWithSelection(): void {
    if (!this.selection) {
      this.syncedSelectionKey = '';
      this.syncedAppointmentKey = '';
      this.draft = this.buildEmptyDraft();
      this.startInputValue = '';
      this.endInputValue = '';
      this.patientQuery = '';
      this.patientSuggestions = [];
      this.availablePatientTreatments = [];
      this.catalogTreatments = [];
      this.selectedExistingTreatmentId = null;
      this.selectedCatalogTreatmentId = null;
      this.conflicts = [];
      return;
    }

    const nextSelectionKey = this.getSelectionKey(this.selection);
    if (nextSelectionKey === this.syncedSelectionKey) {
      return;
    }
    this.syncedSelectionKey = nextSelectionKey;
    this.syncedAppointmentKey = '';

    const selectedCalendar =
      this.calendars.find((calendar) => +calendar.id === +this.selection?.calendar?.id) ||
      this.selection.calendar ||
      this.calendars[0] ||
      null;

    this.draft = {
      title: '',
      notes: '',
      patient: this.initialPatient || null,
      treatments: this.initialPatient ? [...(this.initialTreatments || [])] : [],
      calendar: selectedCalendar,
      start: new Date(this.selection.start),
      end: new Date(this.selection.end),
    };
    this.startInputValue = this.toDateTimeLocalValue(this.draft.start);
    this.endInputValue = this.toDateTimeLocalValue(this.draft.end);
    this.patientQuery = this.getPatientLabel(this.draft.patient);
    this.patientSuggestions = [];
    this.availablePatientTreatments = [];
    this.catalogTreatments = [];
    this.selectedExistingTreatmentId = null;
    this.selectedCatalogTreatmentId = null;
    this.draft.treatments = [];
    if (this.draft.patient?.id) {
      this.loadTreatmentOptions();
    }
    this.conflicts = [];
  }

  public close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
    this.conflicts = [];
    this.saving = false;
  }

  public confirmDeleteAppointment(): void {
    if (!this.appointment?.id || this.saving) {
      return;
    }

    this.confirmationService.confirm({
      message: this.translate.instant('APPOINTMENT.CONFIRM.DELETE.TEXT'),
      header: this.translate.instant('APPOINTMENT.CONFIRM.DELETE.TITLE'),
      icon: 'fas fa-exclamation-triangle',
      acceptLabel: this.translate.instant('COMMON.DELETE'),
      rejectLabel: this.translate.instant('COMMON.CANCEL'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-text',
      accept: () => this.deleteAppointment(),
    });
  }

  private deleteAppointment(): void {
    if (!this.appointment?.id || !this.site || this.saving) {
      return;
    }

    this.saving = true;
    this.appointmentService
      .cancelAppointment(this.customers_id, this.site, this.appointment.id)
      .subscribe({
        next: () => {
          this.saving = false;
          this.deleted.emit();
          this.close();
        },
        error: (error) => {
          this.saving = false;
          this.errorsService.getMessageFromSiteError(error);
        },
      });
  }

  public onCalendarChange(calendarId: number): void {
    this.draft.calendar =
      this.calendars.find((calendar) => +calendar.id === +calendarId) || null;
  }

  private keepSelectedCalendarInSync(): void {
    if (!this.draft.calendar?.id) {
      return;
    }

    const updatedCalendar = this.calendars.find(
      (calendar) => Number(calendar.id || 0) === Number(this.draft.calendar?.id || 0),
    );
    if (updatedCalendar) {
      this.draft.calendar = updatedCalendar;
    }
  }

  public onPatientSelected(patient?: IPatient): void {
    this.draft.patient = patient || null;
    this.patientQuery = this.getPatientLabel(this.draft.patient);
    this.patientSuggestions = [];
    this.clearTreatmentSelection(false);
    if (!this.draft.patient?.id) {
      this.availablePatientTreatments = [];
      this.catalogTreatments = [];
    }
    if (this.draft.patient?.id) {
      this.loadTreatmentOptions();
    }
  }

  private loadAppointmentDetailForEditing(appointmentId: number): void {
    if (
      !this.site ||
      this.loadingAppointmentId === appointmentId ||
      this.loadedAppointmentDetailId === appointmentId
    ) {
      return;
    }

    this.loadingAppointmentId = appointmentId;
    this.appointmentService
      .getAppointment(this.customers_id, this.site, appointmentId)
      .subscribe({
        next: (appointment) => {
          this.loadingAppointmentId = null;
          this.loadedAppointmentDetailId = appointmentId;
          const mergedAppointment = {
            ...this.appointment,
            ...appointment,
          } as IAppointment;
          this.appointment = mergedAppointment;
          this.syncedAppointmentKey = this.getAppointmentKey(
            mergedAppointment,
            this.calendarEvent,
          );
          this.hydrateDraftWithAppointment(mergedAppointment, this.calendarEvent);
        },
        error: (error) => {
          this.loadingAppointmentId = null;
          this.errorsService.getMessageFromSiteError(error);
        },
      });
  }

  private resolveDate(...candidates: Array<Date | string | null | undefined>): Date | null {
    for (const candidate of candidates) {
      if (this.isValidDate(candidate)) {
        return moment(candidate).toDate();
      }
    }

    return null;
  }

  private isValidDate(value: Date | string | null | undefined): boolean {
    return !!value && moment(value).isValid();
  }

  public clearPatientSelection(): void {
    this.onPatientSelected(undefined);
    this.patientQuery = '';
  }

  public onPatientQueryChange(query: string): void {
    this.patientQuery = query || '';

    if (
      this.draft.patient?.id &&
      this.patientQuery !== this.getPatientLabel(this.draft.patient)
    ) {
      this.draft.patient = null;
      this.clearTreatmentSelection(false);
      this.availablePatientTreatments = [];
      this.catalogTreatments = [];
    }

    if (this.patientSearchTimeout) {
      clearTimeout(this.patientSearchTimeout);
      this.patientSearchTimeout = null;
    }

    const normalized = this.patientQuery.trim();
    const siteId = Number(this.sites_id || this.site?.id || 0);
    if (
      !normalized ||
      normalized.length < CalendarAppointmentsMobileFormComponent.MIN_PATIENT_QUERY_LENGTH
    ) {
      this.patientSuggestions = [];
      this.patientSearchLoading = false;
      return;
    }

    if (!this.customers_id || !siteId) {
      this.patientSuggestions = [];
      this.patientSearchLoading = false;
      return;
    }

    this.patientSearchLoading = true;
    this.patientSearchTimeout = setTimeout(() => {
      this.patientsService
        .getBySite(this.customers_id, siteId, { search: normalized })
        .subscribe({
          next: (patients) => {
            this.patientSearchLoading = false;
            this.patientSuggestions = this.normalizePatientResults(patients);
          },
          error: () => {
            this.patientSearchLoading = false;
            this.patientSuggestions = [];
          },
        });
    }, 200);
  }

  private toSearchablePatient(patient: IPatient): SearchablePatient {
    const fullName =
      patient.fullName ||
      `${patient.name || ''} ${patient.surname || ''}`.trim() ||
      patient.email ||
      '';

    return {
      ...patient,
      identifier: patient.email ? `${fullName} (${patient.email})` : fullName,
    };
  }

  private normalizePatientResults(
    payload: unknown,
  ): SearchablePatient[] {
    const rawPatients = this.extractPatientArray(payload);

    const seen = new Set<number | string>();
    return rawPatients
      .filter((patient) => {
        const key = patient?.id || patient?.email || patient?.phone || patient?.name;
        if (!key || seen.has(key)) {
          return false;
        }
        seen.add(key);
        return true;
      })
      .map((patient) => this.toSearchablePatient(patient));
  }

  private extractPatientArray(payload: unknown): IPatient[] {
    if (Array.isArray(payload)) {
      return payload.map((patient) => this.normalizePatientShape(patient));
    }

    if (!payload || typeof payload !== 'object') {
      return [];
    }

    const record = payload as Record<string, unknown>;
    const directKeys = ['data', 'patients', 'items', 'results', 'rows', 'records'];
    for (const key of directKeys) {
      if (Array.isArray(record[key])) {
        return (record[key] as unknown[]).map((patient) =>
          this.normalizePatientShape(patient),
        );
      }
    }

    const nestedArray = Object.values(record).find((value) => Array.isArray(value));
    return Array.isArray(nestedArray)
      ? nestedArray.map((patient) => this.normalizePatientShape(patient))
      : [];
  }

  private normalizePatientShape(patient: unknown): IPatient {
    const record =
      patient && typeof patient === 'object' ? (patient as Record<string, unknown>) : {};
    const name = this.readString(record, ['name', 'first_name', 'firstname', 'fullName', 'full_name']);
    const surname = this.readString(record, ['surname', 'last_name', 'lastname']);
    const fullName = this.readString(record, ['fullName', 'full_name', 'display_name', 'identifier']);

    return {
      ...((record as unknown) as Partial<IPatient>),
      id: Number(record['id'] || record['patients_id'] || record['patient_id'] || 0) || undefined,
      name: name || fullName || this.readString(record, ['email', 'phone']),
      surname,
      email: this.readString(record, ['email']),
      fiscal_id: this.readString(record, ['fiscal_id', 'fiscalId', 'document']),
      phone: this.readString(record, ['phone', 'mobile', 'telephone']),
      fullName: fullName || `${name || ''} ${surname || ''}`.trim(),
    };
  }

  private readString(record: Record<string, unknown>, keys: string[]): string {
    for (const key of keys) {
      const value = record[key];
      if (value !== null && value !== undefined && `${value}`.trim()) {
        return `${value}`.trim();
      }
    }
    return '';
  }

  public selectExistingTreatment(treatmentId: number): void {
    const normalizedId = Number(treatmentId || 0);
    this.selectedExistingTreatmentId =
      this.selectedExistingTreatmentId === normalizedId ? null : normalizedId;
    this.selectedCatalogTreatmentId = null;
    this.draft.treatments = this.selectedExistingTreatmentId
      ? this.availablePatientTreatments.filter(
          (treatment) => Number(treatment.id || 0) === this.selectedExistingTreatmentId,
        )
      : [];

    if (this.selectedExistingTreatmentId) {
      this.applyTreatmentCalendar(this.draft.treatments[0]?.treatment || null);
    }
  }

  public selectCatalogTreatment(treatmentId: number | null): void {
    const normalizedId = Number(treatmentId || 0);
    if (!normalizedId) {
      this.selectedCatalogTreatmentId = null;
      this.selectedExistingTreatmentId = null;
      this.draft.treatments = [];
      return;
    }

    this.selectedCatalogTreatmentId =
      this.selectedCatalogTreatmentId === normalizedId ? null : normalizedId;
    this.selectedExistingTreatmentId = null;
    this.draft.treatments = [];

    if (this.selectedCatalogTreatmentId) {
      const selectedTreatment = this.catalogTreatments.find(
        (treatment) => Number(treatment.id || 0) === this.selectedCatalogTreatmentId,
      );
      this.applyTreatmentCalendar(selectedTreatment || null);
    }
  }

  public clearTreatmentSelection(clearLoadedOptions = true): void {
    this.selectedExistingTreatmentId = null;
    this.selectedCatalogTreatmentId = null;
    this.draft.treatments = [];
    if (clearLoadedOptions) {
      this.availablePatientTreatments = [];
      this.catalogTreatments = [];
      if (this.draft.patient?.id) {
        this.loadTreatmentOptions();
      }
    }
  }

  private applyTreatmentCalendar(treatment?: ITreatment | null): void {
    const calendarId = Number(
      treatment?.booking_calendars_id || treatment?.bookingCalendar?.id || 0,
    );
    if (!calendarId) {
      return;
    }

    const calendar = this.calendars.find(
      (item) => Number(item.id || 0) === calendarId,
    );
    if (calendar) {
      this.draft.calendar = calendar;
    }
  }

  public onStartChanged(value: string): void {
    this.startInputValue = value || '';
    this.draft.start = this.fromDateTimeLocalValue(this.startInputValue);
    this.ensureValidRange('start');
    this.syncDateInputsFromDraft();
  }

  public onEndChanged(value: string): void {
    this.endInputValue = value || '';
    this.draft.end = this.fromDateTimeLocalValue(this.endInputValue);
    this.ensureValidRange('end');
    this.syncDateInputsFromDraft();
  }

  private ensureValidRange(changedField: 'start' | 'end'): void {
    if (!this.draft.start && this.draft.end) {
      this.draft.start = new Date(this.draft.end);
    }
    if (!this.draft.end && this.draft.start) {
      this.draft.end = new Date(
        new Date(this.draft.start).getTime() +
          CalendarAppointmentsMobileFormComponent.DEFAULT_DURATION_MINUTES * 60000,
      );
    }
    if (!this.draft.start || !this.draft.end) {
      return;
    }

    if (this.draft.end.getTime() <= this.draft.start.getTime()) {
      const nextEnd = new Date(this.draft.start);
      nextEnd.setMinutes(
        nextEnd.getMinutes() +
          CalendarAppointmentsMobileFormComponent.DEFAULT_DURATION_MINUTES,
      );
      if (changedField === 'end') {
        this.draft.start = new Date(
          this.draft.end.getTime() -
            CalendarAppointmentsMobileFormComponent.DEFAULT_DURATION_MINUTES * 60000,
        );
      } else {
        this.draft.end = nextEnd;
      }
    }
  }

  private syncDateInputsFromDraft(): void {
    this.startInputValue = this.toDateTimeLocalValue(this.draft.start);
    this.endInputValue = this.toDateTimeLocalValue(this.draft.end);
  }

  private toDateTimeLocalValue(date: Date | null): string {
    if (!date) {
      return '';
    }
    return moment(date).format('YYYY-MM-DD[T]HH:mm');
  }

  private fromDateTimeLocalValue(value: string): Date | null {
    if (!value) {
      return null;
    }
    const parsed = moment(value, 'YYYY-MM-DD[T]HH:mm', true);
    return parsed.isValid() ? parsed.toDate() : null;
  }

  private getPatientLabel(patient?: IPatient | null): string {
    if (!patient) {
      return '';
    }
    return [patient.name, patient.surname].filter(Boolean).join(' ');
  }

  private rebuildCalendarOptions(): void {
    this.calendarOptions = (this.calendars || []).map((calendar) => ({
      id: Number(calendar.id || 0),
      name: calendar.name,
    }));
  }

  private getSelectionKey(selection: MobileAppointmentSelection): string {
    return [
      selection.source,
      selection.start?.getTime?.() ?? new Date(selection.start).getTime(),
      selection.end?.getTime?.() ?? new Date(selection.end).getTime(),
      selection.calendar?.id || '',
    ].join('|');
  }

  private getAppointmentKey(
    appointment: IAppointment,
    calendarEvent?: CalendarEvent | null,
  ): string {
    return [
      appointment.id || '',
      this.resolveDate(appointment.start, calendarEvent?.start)?.getTime() || '',
      this.resolveDate(appointment.end, calendarEvent?.end)?.getTime() || '',
      appointment.calendar?.id || appointment.calendars_id || '',
      appointment.patient?.id || appointment.patients_id || '',
      appointment.treatments?.length || '',
      appointment.name || '',
      appointment.description || '',
    ].join('|');
  }

  private buildCreationDraft(): AppointmentCreationDraft | null {
    if (!this.draft.calendar || !this.draft.start || !this.draft.end) {
      return null;
    }

    return {
      calendar: this.draft.calendar,
      start: this.draft.start,
      end: this.draft.end,
      patient: this.draft.patient,
      title: this.draft.title,
      notes: this.draft.notes,
      treatments: [...(this.draft.treatments || [])],
      price: 0,
    };
  }

  public save(): void {
    const creationDraft = this.buildCreationDraft();
    if (!creationDraft || this.saving) {
      return;
    }

    this.saving = true;
    this.conflicts = [];

    if (this.isEditing) {
      this.updateExistingAppointment(creationDraft);
      return;
    }

    this.appointmentCreationService
      .findConflicts(
        this.customers_id,
        this.site,
        creationDraft.calendar,
        creationDraft.start,
        creationDraft.end,
      )
      .subscribe({
        next: (conflicts) => {
          if (conflicts?.length) {
            this.saving = false;
            this.conflicts = conflicts;
            this.messageService.add({
              severity: 'warn',
              summary: this.translate.instant(
                'APPOINTMENT.MOBILE.CONFLICTS.SUMMARY',
              ),
              detail: this.translate.instant(
                'APPOINTMENT.MOBILE.CONFLICTS.DETAIL',
              ),
            });
            return;
          }

          this.resolveTreatmentsForSave(creationDraft).subscribe({
            next: (resolvedDraft) => {
              this.registerDraft(resolvedDraft);
            },
            error: () => {
              this.saving = false;
              this.messageService.add({
                severity: 'error',
                summary: this.translate.instant(
                  'MESSAGES.APPOINTMENTS.ERROR.SUMMARY',
                ),
                detail: this.translate.instant('ERRORS.ADDING_TREATMENT'),
              });
            },
          });
        },
        error: () => {
          this.saving = false;
        },
      });
  }

  private updateExistingAppointment(creationDraft: AppointmentCreationDraft): void {
    if (!this.appointment?.id || !this.site?.id) {
      this.saving = false;
      return;
    }

    const appointment = this.appointmentCreationService.buildAppointment(creationDraft);
    if (!appointment) {
      this.saving = false;
      return;
    }

    (appointment as Partial<IAppointment>).patients_id =
      creationDraft.patient?.id || null;

    this.appointmentService
      .update(this.customers_id, Number(this.site.id), this.appointment.id, appointment)
      .pipe(
        switchMap(() => this.resolveTreatmentsForSave(creationDraft)),
        switchMap((resolvedDraft) => this.syncExistingAppointmentTreatments(resolvedDraft)),
      )
      .subscribe({
        next: () => {
          this.saving = false;
          this.saved.emit();
          this.close();
        },
        error: (error) => {
          this.saving = false;
          this.errorsService.getMessageFromSiteError(error);
        },
      });
  }

  private syncExistingAppointmentTreatments(
    draft: AppointmentCreationDraft,
  ): Observable<unknown> {
    if (!this.appointment?.id || !this.site?.id) {
      return of(null);
    }

    const currentTreatments = this.appointment.treatments || [];
    const nextTreatments = draft.treatments || [];
    const nextIds = new Set(nextTreatments.map((item) => Number(item.id || 0)));
    const currentIds = new Set(currentTreatments.map((item) => Number(item.id || 0)));
    const requests: Observable<unknown>[] = [];

    currentTreatments
      .filter((item) => item.id && !nextIds.has(Number(item.id)))
      .forEach((item) => {
        requests.push(
          this.appointmentService.removeFromAppointment(
            this.customers_id,
            Number(this.site!.id),
            this.appointment!.id!,
            item.id,
          ),
        );
      });

    nextTreatments
      .filter((item) => item.id && !currentIds.has(Number(item.id)))
      .forEach((item) => {
        requests.push(
          this.appointmentService.addTreatment(
            this.customers_id,
            Number(this.site!.id),
            this.appointment!.id!,
            item,
          ),
        );
      });

    return requests.length ? forkJoin(requests) : of(null);
  }

  private loadTreatmentOptions(): void {
    if (!this.draft.patient?.id || !this.customers_id || !this.site?.id) {
      this.availablePatientTreatments = [];
      this.catalogTreatments = [];
      return;
    }

    this.treatmentOptionsLoading = true;
    forkJoin({
      available: this.patientsService.getTreatments(
        this.customers_id,
        this.draft.patient.id,
        Number(this.site.id),
      ),
      catalog: this.treatmentsService.getFromSite(this.customers_id, this.site),
    }).subscribe({
      next: ({ available, catalog }) => {
        this.availablePatientTreatments = (available || []).filter(
          (treatment) => !treatment.appointment,
        );
        this.catalogTreatments = (catalog || []).filter(
          (treatment) => treatment.active !== false,
        );
        this.treatmentOptionsLoading = false;
      },
      error: () => {
        this.availablePatientTreatments = [];
        this.catalogTreatments = [];
        this.treatmentOptionsLoading = false;
      },
    });
  }

  private resolveTreatmentsForSave(
    draft: AppointmentCreationDraft,
  ): Observable<AppointmentCreationDraft> {
    if (draft.treatments?.length || !this.selectedCatalogTreatmentId) {
      return of(draft);
    }

    const patient = draft.patient;
    const treatment = this.catalogTreatments.find(
      (item) => Number(item.id || 0) === Number(this.selectedCatalogTreatmentId || 0),
    );

    if (!patient?.id || !treatment?.id) {
      return of(draft);
    }

    const existingIds = new Set(
      this.availablePatientTreatments.map((item) => Number(item.id || 0)),
    );

    return this.patientsService
      .addTreatment(this.customers_id, patient, treatment, {
        units: 1,
        price: Number(treatment.price || 0),
      })
      .pipe(
        switchMap(() =>
          this.patientsService.getTreatments(
            this.customers_id,
            patient.id!,
            Number(this.site?.id || 0),
          ),
        ),
        map((treatments) => {
          const available = (treatments || []).filter(
            (item) =>
              !item.appointment &&
              Number(item.treatments_id || 0) === Number(treatment.id || 0),
          );
          const resolved =
            available.find((item) => !existingIds.has(Number(item.id || 0))) ||
            [...available].sort((a, b) => Number(b.id || 0) - Number(a.id || 0))[0];

          if (!resolved) {
            throw new Error('Unable to resolve created patient treatment');
          }

          this.availablePatientTreatments = (treatments || []).filter(
            (item) => !item.appointment,
          );

          return {
            ...draft,
            treatments: [resolved],
          };
        }),
      );
  }

  private registerDraft(draft: AppointmentCreationDraft): void {
    this.appointmentCreationService
      .registerDraft(this.customers_id, this.site, draft)
      .subscribe({
        next: () => {
          this.saving = false;
          this.calendarAppointmentsService.emitPatientAppointments$();
          this.saved.emit();
          this.close();
        },
        error: (error) => {
          this.saving = false;
          const code = this.errorsService.getMessageFromSiteError(error);
          this.messageService.add({
            detail: this.translate.instant(`MESSAGES.APPOINTMENTS.ERROR.${code}`),
            summary: this.translate.instant('MESSAGES.APPOINTMENTS.ERROR.SUMMARY'),
            severity: 'error',
          });
        },
      });
  }
}
